// Read-side helpers for the raw `get-user-profile-details` payload, plus the
// prefetch that seeds it into myUserProfileCache before a screen needs it.
//
// The cache deliberately stores the RAW payload (ProfileCard's
// transformPayloadToProfileData returns its input unchanged as `payload`), so a
// prefetch here and a fetch inside ProfileCard are interchangeable.

import { getUserProfileDetails } from "../service/AuthWellDhanService";
import { myUserProfileCache, serializeUserProfileKey } from "./listCache/caches";
import {
  getLatestEntry,
  calculateAge,
  calculateBMI,
  calculateIdealWeight,
  calculateBMR,
  calculateTDEE,
  convertHeightToCm,
  convertWeightToKg,
} from "./bodyMetrics";

/** Description of the event type that fetches a user profile. */
const PROFILE_EVENT_DESCRIPTION = "get_user_profile_details";

/**
 * Resolve the profile event type id out of session utils. Several screens do
 * this lookup by description; centralised so the magic string lives once.
 *
 * @param {Object} sessionUtils
 * @returns {string|undefined}
 */
export function resolveProfileEventTypeId(sessionUtils) {
  return sessionUtils?.event_types?.[0]?.event_types?.find(
    (x) => x.description === PROFILE_EVENT_DESCRIPTION
  )?.id;
}

/** In-flight prefetches, so hovering a row repeatedly fires one request. */
const inFlight = new Map();

/**
 * Warm `myUserProfileCache` for one user. Resolves to true when the cache
 * holds a payload afterwards.
 *
 * Never throws and never surfaces an error: a failed prefetch just means
 * ProfileCard will fetch normally when the panel opens, which is the
 * pre-existing behaviour.
 *
 * @param {string} eventTypeId
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export function prefetchUserProfile(eventTypeId, userId) {
  if (!eventTypeId || !userId) return Promise.resolve(false);

  const key = serializeUserProfileKey(userId);
  // isFresh rather than get: get would touch LRU order for a user who may
  // never actually be opened.
  if (myUserProfileCache.isFresh(key)) return Promise.resolve(true);

  const existing = inFlight.get(key);
  if (existing) return existing;

  const request = getUserProfileDetails(eventTypeId, userId)
    .then((res) => {
      if (!res?.success || !res.data) return false;
      const payload = res.data.data ?? res.data;
      if (!payload) return false;
      myUserProfileCache.set(key, payload);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
}

/** "Female" -> "F". Falls back to the raw string when it isn't M/F. */
function shortGender(description) {
  if (!description) return null;
  const lower = description.toLowerCase();
  if (lower.startsWith("f")) return "F";
  if (lower.startsWith("m")) return "M";
  return description;
}

/**
 * Every description in a join table, e.g. dietary_preferences. Both shapes are
 * tolerated: `{ dietary_preference: { description } }` and a flat
 * `{ description }`.
 *
 * @param {Array<Object>|undefined} rows
 * @param {string} nestedKey
 * @returns {string[]}
 */
function descriptions(rows, nestedKey) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => row?.[nestedKey]?.description || row?.description || null)
    .filter(Boolean);
}

/**
 * The facts a coach checks constantly, from a raw profile payload.
 *
 * Every field is independently nullable (lists default to empty) — a partial
 * profile renders the parts it has rather than a row of placeholders. Derived
 * metrics come from `utils/bodyMetrics`, the same module ProfileCard uses, so
 * the compact strip and the full card can never disagree.
 *
 * @param {Object|null|undefined} payload - Raw get-user-profile-details data.
 * @returns {Object|null} Null when there is no payload at all.
 */
function selectProfileDigest(payload) {
  if (!payload) return null;

  const profile = payload.profile || {};
  const height = getLatestEntry(payload.heights);
  const weight = getLatestEntry(payload.weights);
  const heightUnits = height?.height_unit?.description || "cm";
  const weightUnits = weight?.weight_unit?.description || "kg";
  const rawHeight = height?.height;
  const rawWeight = weight?.weight;
  const genderDescription = profile.gender?.description || null;

  const heightCm = convertHeightToCm(rawHeight, heightUnits);
  const weightKg = convertWeightToKg(rawWeight, weightUnits);
  const age = calculateAge(profile.date_of_birth);
  const bmr = calculateBMR(
    rawWeight,
    rawHeight,
    age,
    genderDescription,
    weightUnits,
    heightUnits
  );

  const name =
    [profile.first_name, profile.last_name].filter(Boolean).join(" ") || null;
  const phoneParts = [
    payload.phones?.[0]?.phone_extension?.description,
    payload.phones?.[0]?.phone_number,
  ].filter(Boolean);

  return {
    name,
    age,
    gender: shortGender(genderDescription),
    genderFull: genderDescription,
    heightCm: heightCm == null ? null : Math.round(heightCm),
    // One decimal: coaches track weight at this precision (79.4, not 79).
    weightKg: weightKg == null ? null : Math.round(weightKg * 10) / 10,
    bmi: calculateBMI(rawWeight, rawHeight, weightUnits, heightUnits),
    idealWeightKg: calculateIdealWeight(rawHeight, genderDescription, heightUnits),
    bmr,
    tdee: calculateTDEE(bmr),
    email: payload.emails?.[0]?.email_address || null,
    phone: phoneParts.length ? phoneParts.join(" ") : null,
    dietaryPreferences: descriptions(
      payload.dietary_preferences,
      "dietary_preference"
    ),
    fitnessGoals: descriptions(payload.fitness_goals, "fitness_goal"),
    medicalHistories: descriptions(payload.medical_histories, "medical_history"),
    injuries: descriptions(payload.physical_injuries, "physical_injury"),
  };
}

/**
 * Digest for a user already in the cache, or null when not cached.
 * @param {string} userId
 * @returns {ReturnType<typeof selectProfileDigest>}
 */
export function peekProfileDigest(userId) {
  const entry = myUserProfileCache.peek(serializeUserProfileKey(userId));
  return entry ? selectProfileDigest(entry.data) : null;
}
